import { useQuery } from '@apollo/client';

import { deslugify, slugify } from '@xing-com/crate-jobs-domain-serps-helpers';
import { GetLocationSuggestionsDocument } from '@xing-com/crate-jobs-graphql';
import type { GetLocationSuggestionsQuery } from '@xing-com/crate-jobs-graphql';

const LOCATION_AUTOCOMPLETE_CONSUMER = 'react.location.search.jobs.form';
const LOCATION_COUNTRIES = ['de', 'at', 'ch'];
const LOCATION_SUGGESTIONS_LIMIT = 5;
const CANONICAL_CITY_LANGUAGE = 'de';

const getLiteralAutocompleteText = (citySlug: string | undefined): string =>
  citySlug ? deslugify(citySlug) : '';

const getTransliteratedAutocompleteText = (
  citySlug: string | undefined
): string =>
  citySlug
    ? getLiteralAutocompleteText(citySlug).replace(
        /([bcdfghjklmnpqrstvwxyz])(ae|oe|ue)/gi,
        (_match, consonant: string, digraph: string) => {
          const umlauts: Record<string, string> = {
            ae: 'ä',
            oe: 'ö',
            ue: 'ü',
          };

          return `${consonant}${umlauts[digraph.toLowerCase()]}`;
        }
      )
    : '';

const normalizeCitySlug = (cityName: string): string =>
  slugify(cityName)
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss');

type LocationSuggestion = NonNullable<
  NonNullable<
    GetLocationSuggestionsQuery['autocompletionLocationsCity']
  >['collection'][number]
>;

export const useCityAdminArea = (
  citySlug: string | undefined,
  language: string
): string | undefined => {
  const literalRequestText = getLiteralAutocompleteText(citySlug);
  const requestText = getTransliteratedAutocompleteText(citySlug);
  const normalizedCitySlug = citySlug ? normalizeCitySlug(citySlug) : undefined;

  // SERP city slugs use the canonical German spelling. Resolve the route to a
  // stable city ID in German, independently of the localized display name.
  const {
    data: canonicalData,
    error: canonicalError,
    loading: canonicalLoading,
  } = useQuery(GetLocationSuggestionsDocument, {
    variables: {
      consumer: LOCATION_AUTOCOMPLETE_CONSUMER,
      text: requestText,
      language: CANONICAL_CITY_LANGUAGE,
      countries: LOCATION_COUNTRIES,
      limit: LOCATION_SUGGESTIONS_LIMIT,
    },
    skip: !citySlug,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const canonicalSuggestions =
    canonicalData?.autocompletionLocationsCity?.collection ?? [];
  const hasExactCanonicalSuggestion = canonicalSuggestions.some(
    (suggestion) =>
      suggestion?.name &&
      normalizeCitySlug(suggestion.name) === normalizedCitySlug
  );
  const shouldQueryLiteralText =
    Boolean(citySlug) &&
    literalRequestText !== requestText &&
    !canonicalLoading &&
    !hasExactCanonicalSuggestion;
  const { data: literalData, error: literalError } = useQuery(
    GetLocationSuggestionsDocument,
    {
      variables: {
        consumer: LOCATION_AUTOCOMPLETE_CONSUMER,
        text: literalRequestText,
        language: CANONICAL_CITY_LANGUAGE,
        countries: LOCATION_COUNTRIES,
        limit: LOCATION_SUGGESTIONS_LIMIT,
      },
      skip: !shouldQueryLiteralText,
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    }
  );

  const canonicalSource = hasExactCanonicalSuggestion
    ? canonicalSuggestions
    : (literalData?.autocompletionLocationsCity?.collection ?? []);
  const canonicalLookupError = hasExactCanonicalSuggestion
    ? canonicalError
    : shouldQueryLiteralText
      ? literalError
      : canonicalError;
  const canonicalCitySuggestions: readonly LocationSuggestion[] =
    canonicalLookupError || canonicalLoading
      ? []
      : canonicalSource.filter((suggestion): suggestion is LocationSuggestion =>
          Boolean(
            suggestion?.name &&
            suggestion.cityIdWithScrambling &&
            normalizeCitySlug(suggestion.name) === normalizedCitySlug
          )
        );
  const canonicalCityIds = new Set(
    canonicalCitySuggestions.map(
      (suggestion) => suggestion?.cityIdWithScrambling
    )
  );
  const canonicalCityId =
    canonicalCityIds.size === 1 ? [...canonicalCityIds][0] : undefined;
  const canonicalCityName = canonicalCitySuggestions.find(
    (suggestion) => suggestion?.cityIdWithScrambling === canonicalCityId
  )?.name;

  const {
    data: localizedData,
    error: localizedError,
    loading: localizedLoading,
  } = useQuery(GetLocationSuggestionsDocument, {
    variables: {
      consumer: LOCATION_AUTOCOMPLETE_CONSUMER,
      text: canonicalCityName ?? requestText,
      language,
      countries: LOCATION_COUNTRIES,
      limit: LOCATION_SUGGESTIONS_LIMIT,
    },
    skip: !citySlug || language === CANONICAL_CITY_LANGUAGE || !canonicalCityId,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  if (
    canonicalLookupError ||
    localizedError ||
    localizedLoading ||
    !canonicalCityId
  ) {
    return undefined;
  }

  const localizedSuggestions: readonly (LocationSuggestion | null)[] =
    language === CANONICAL_CITY_LANGUAGE
      ? canonicalCitySuggestions
      : (localizedData?.autocompletionLocationsCity?.collection ?? []);
  const matchingSuggestions = localizedSuggestions.filter(
    (suggestion): suggestion is NonNullable<LocationSuggestion> =>
      Boolean(
        suggestion?.cityIdWithScrambling === canonicalCityId &&
        suggestion.adminAreaIdWithScrambling &&
        suggestion.adminArea
      )
  );

  if (
    matchingSuggestions.some(
      (suggestion) =>
        suggestion.cityIdWithScrambling === suggestion.adminAreaIdWithScrambling
    )
  ) {
    return undefined;
  }

  const adminAreas = new Map(
    matchingSuggestions.map((suggestion) => [
      suggestion.adminAreaIdWithScrambling,
      suggestion.adminArea,
    ])
  );

  const adminArea =
    adminAreas.size === 1 ? [...adminAreas.values()][0] : undefined;

  return typeof adminArea === 'string' ? adminArea : undefined;
};
